import React, { useState } from 'react';
import {
  Database, Search, Download, Eye, Edit3, Trash2,
  User, Clock, Shield, RefreshCw
} from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useFormat } from '../../i18n/format.js';
import { apiRequest } from '../../api/core.js';

// Seuls l'icône et la couleur restent des constantes : le libellé est résolu au
// rendu, sinon il resterait figé dans la langue du chargement du module.
const ACTION_TYPES = {
  view: { icon: Eye, color: 'blue' },
  create: { icon: Database, color: 'green' },
  update: { icon: Edit3, color: 'amber' },
  delete: { icon: Trash2, color: 'red' },
  export: { icon: Download, color: 'purple' },
  access_request: { icon: Shield, color: 'cyan' }
};

const RESOURCE_TYPES = ['driver', 'vehicle', 'location', 'telematics', 'claim', 'consent', 'performance'];

function AuditLogViewer({ onMessage }) {
  const { t, fmt } = useFormat();
  const [filters, setFilters] = useState({
    resourceType: '',
    actionType: '',
    gdprOnly: false,
    search: ''
  });
  const [page, setPage] = useState(0);
  const pageSize = 50;

  // Fetch audit logs (tenant-isolated via middleware)
  const { data: logsData, isLoading, refetch } = useQuery({
    queryKey: ['audit_logs', page, filters],
    queryFn: async () => {
      const params = new URLSearchParams({
        limit: pageSize,
        offset: page * pageSize
      });
      if (filters.resourceType) params.append('resource_type', filters.resourceType);
      if (filters.gdprOnly) params.append('gdpr_relevant', '1');

      return apiRequest(`/gdpr-compliance/audit-logs?${params}`);
    }
  });

  const logs = logsData?.data || [];

  const filteredLogs = logs.filter(log => {
    if (filters.actionType && log.action_type !== filters.actionType) return false;
    if (filters.search) {
      const searchLower = filters.search.toLowerCase();
      return (
        log.user_email?.toLowerCase().includes(searchLower) ||
        log.resource_description?.toLowerCase().includes(searchLower)
      );
    }
    return true;
  });

  const exportLogs = () => {
    const csv = [
      [
        t('compliance.audit.csv.date'),
        t('compliance.audit.csv.user'),
        t('compliance.audit.csv.action'),
        t('compliance.audit.csv.resource'),
        t('compliance.audit.csv.id'),
        t('compliance.audit.csv.description'),
        t('compliance.audit.csv.gdpr'),
      ].join(','),
      ...filteredLogs.map(log => [
        fmt.dateTime(log.timestamp),
        log.user_email || t('compliance.audit.systemUser'),
        log.action_type,
        log.resource_type,
        log.resource_id,
        `"${log.resource_description || ''}"`,
        log.gdpr_relevant ? t('common.yes') : t('common.no')
      ].join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `audit_log_${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold flex items-center">
            <Database className="w-5 h-5 mr-2 text-blue-600" />
            {t('compliance.audit.title')}
          </h3>
          <p className="text-sm text-gray-500">
            {t('compliance.audit.subtitle')}
          </p>
        </div>
        <div className="flex items-center space-x-2">
          <button
            onClick={() => refetch()}
            className="p-2 text-gray-500 hover:text-gray-700"
            title={t('common.actions.refresh')}
          >
            <RefreshCw className="w-5 h-5" />
          </button>
          <button
            onClick={exportLogs}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 flex items-center"
          >
            <Download className="w-4 h-4 mr-2" />
            {t('compliance.audit.exportCsv')}
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex flex-wrap gap-4">
          <div className="flex items-center">
            <Search className="w-4 h-4 text-gray-400 mr-2" />
            <input
              type="text"
              placeholder={t('common.search.placeholder')}
              value={filters.search}
              onChange={e => setFilters({ ...filters, search: e.target.value })}
              className="border rounded-lg px-3 py-2 w-64"
            />
          </div>

          <select
            value={filters.resourceType}
            onChange={e => setFilters({ ...filters, resourceType: e.target.value })}
            className="border rounded-lg px-3 py-2"
          >
            <option value="">{t('compliance.audit.filters.allResources')}</option>
            {RESOURCE_TYPES.map(key => (
              <option key={key} value={key}>{t(`compliance.audit.resources.${key}`)}</option>
            ))}
          </select>

          <select
            value={filters.actionType}
            onChange={e => setFilters({ ...filters, actionType: e.target.value })}
            className="border rounded-lg px-3 py-2"
          >
            <option value="">{t('compliance.audit.filters.allActions')}</option>
            {Object.keys(ACTION_TYPES).map(key => (
              <option key={key} value={key}>{t(`compliance.audit.actions.${key}`)}</option>
            ))}
          </select>

          <label className="flex items-center">
            <input
              type="checkbox"
              checked={filters.gdprOnly}
              onChange={e => setFilters({ ...filters, gdprOnly: e.target.checked })}
              className="mr-2"
            />
            <span className="text-sm">{t('compliance.audit.filters.gdprOnly')}</span>
          </label>
        </div>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <StatCard
          title={t('compliance.audit.stats.views')}
          value={logs.filter(l => l.action_type === 'view').length}
          icon={Eye}
          color="blue"
        />
        <StatCard
          title={t('compliance.audit.stats.updates')}
          value={logs.filter(l => l.action_type === 'update').length}
          icon={Edit3}
          color="amber"
        />
        <StatCard
          title={t('compliance.audit.stats.deletes')}
          value={logs.filter(l => l.action_type === 'delete').length}
          icon={Trash2}
          color="red"
        />
        <StatCard
          title={t('compliance.audit.stats.exports')}
          value={logs.filter(l => l.action_type === 'export').length}
          icon={Download}
          color="purple"
        />
      </div>

      {/* Logs Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50">
            <tr>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('compliance.audit.table.datetime')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('compliance.audit.table.user')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('compliance.audit.table.action')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('compliance.audit.table.resource')}</th>
              <th className="px-4 py-3 text-left text-sm font-medium text-gray-700">{t('compliance.audit.table.description')}</th>
              <th className="px-4 py-3 text-center text-sm font-medium text-gray-700">{t('compliance.audit.table.gdpr')}</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200">
            {isLoading ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  {t('common.states.loading')}
                </td>
              </tr>
            ) : filteredLogs.length === 0 ? (
              <tr>
                <td colSpan={6} className="px-4 py-8 text-center text-gray-500">
                  {t('compliance.audit.empty')}
                </td>
              </tr>
            ) : (
              filteredLogs.map(log => {
                const actionConfig = ACTION_TYPES[log.action_type] || ACTION_TYPES.view;
                const ActionIcon = actionConfig.icon;

                return (
                  <tr key={log.id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center text-gray-500">
                        <Clock className="w-4 h-4 mr-1" />
                        {fmt.dateTime(log.timestamp)}
                      </div>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <div className="flex items-center">
                        <div className="w-8 h-8 bg-gray-100 rounded-full flex items-center justify-center mr-2">
                          <User className="w-4 h-4 text-gray-500" />
                        </div>
                        <div>
                          <div className="font-medium">{log.user_email || t('compliance.audit.systemUser')}</div>
                          {log.user_role && (
                            <div className="text-xs text-gray-500">{log.user_role}</div>
                          )}
                        </div>
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-medium bg-${actionConfig.color}-100 text-${actionConfig.color}-800`}>
                        <ActionIcon className="w-3 h-3 mr-1" />
                        {t(`compliance.audit.actions.${ACTION_TYPES[log.action_type] ? log.action_type : 'view'}`)}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 bg-gray-100 rounded text-gray-700">
                        {t(`compliance.audit.resources.${log.resource_type}`, { defaultValue: log.resource_type })}
                      </span>
                      {log.resource_id && (
                        <span className="ml-1 text-gray-500">#{log.resource_id}</span>
                      )}
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-600 max-w-xs truncate">
                      {log.resource_description || '-'}
                    </td>
                    <td className="px-4 py-3 text-center">
                      {log.gdpr_relevant ? (
                        <Shield className="w-5 h-5 text-blue-600 mx-auto" />
                      ) : (
                        <span className="text-gray-300">-</span>
                      )}
                    </td>
                  </tr>
                );
              })
            )}
          </tbody>
        </table>

        {/* Pagination */}
        <div className="px-4 py-3 border-t bg-gray-50 flex items-center justify-between">
          <span className="text-sm text-gray-500">
            {t('compliance.audit.pagination.showing', {
              start: page * pageSize + 1,
              end: Math.min((page + 1) * pageSize, logs.length),
            })}
          </span>
          <div className="flex space-x-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
            >
              {t('common.actions.previous')}
            </button>
            <button
              onClick={() => setPage(page + 1)}
              disabled={logs.length < pageSize}
              className="px-3 py-1 border rounded text-sm disabled:opacity-50"
            >
              {t('common.actions.next')}
            </button>
          </div>
        </div>
      </div>

      {/* Info */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <Shield className="w-5 h-5 text-blue-600 mr-3 mt-0.5" />
          <div>
            <h4 className="font-semibold text-blue-800">{t('compliance.audit.info.title')}</h4>
            <p className="text-sm text-blue-700 mt-1">
              {t('compliance.audit.info.body')}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}

function StatCard({ title, value, icon: Icon, color }) {
  const colorClasses = {
    blue: 'bg-blue-100 text-blue-600',
    amber: 'bg-amber-100 text-amber-600',
    red: 'bg-red-100 text-red-600',
    purple: 'bg-purple-100 text-purple-600'
  };

  return (
    <div className="bg-white rounded-lg shadow p-4">
      <div className="flex items-center justify-between">
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <p className="text-2xl font-bold">{value}</p>
        </div>
        <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${colorClasses[color]}`}>
          <Icon className="w-5 h-5" />
        </div>
      </div>
    </div>
  );
}

export default AuditLogViewer;
